import {
  isDeletedRecipe,
  isDemoRecipe,
  isOwnRecipe,
  isStrictlyPublicRecipe,
  isSuccessfullyCalculated
} from "./recipeList.utils";
import { RecipeRecord } from "./types";

export interface RecipeRowActions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canMakePublic: boolean;
  canMakePrivate: boolean;
  canToggleDemoKit: boolean;
  canViewDemoKitUsage: boolean;
  isDemoKitToggleDisabled: boolean;
  isViewOnly: boolean;
}

export interface RecipeActionContext {
  isAdmin: boolean;
  currentUserId?: string;
}

export const getCurrentUserId = (user: { _id?: string; id?: string } | null | undefined) =>
  user?._id ?? user?.id;

export const getRecipeRowActions = (
  recipe: RecipeRecord,
  context: RecipeActionContext
): RecipeRowActions => {
  const { isAdmin, currentUserId } = context;
  const isOwner = isOwnRecipe(recipe, currentUserId);
  const isCalculated = isSuccessfullyCalculated(recipe);
  const demo = isDemoRecipe(recipe);
  const strictlyPublic = isStrictlyPublicRecipe(recipe);
  const deleted = isDeletedRecipe(recipe);

  const viewOnly = (canView = true): RecipeRowActions => ({
    canView,
    canEdit: false,
    canDelete: false,
    canMakePublic: false,
    canMakePrivate: false,
    canToggleDemoKit: false,
    canViewDemoKitUsage: false,
    isDemoKitToggleDisabled: true,
    isViewOnly: true
  });

  if (deleted) {
    return {
      ...viewOnly(false),
      canViewDemoKitUsage: isAdmin && demo
    };
  }

  if (demo && !isAdmin && !isOwner) {
    return viewOnly();
  }

  if (!isAdmin && !isOwner && strictlyPublic) {
    return viewOnly();
  }

  if (!isAdmin && isOwner) {
    return {
      canView: true,
      canEdit: true,
      canDelete: isOwner,
      canMakePublic: isOwner && isCalculated && !demo && !strictlyPublic,
      canMakePrivate: isOwner && strictlyPublic && !demo,
      canToggleDemoKit: false,
      canViewDemoKitUsage: false,
      isDemoKitToggleDisabled: true,
      isViewOnly: false
    };
  }

  if (isAdmin) {
    return {
      canView: true,
      canEdit: isOwner || isAdmin,
      canDelete: isOwner || isAdmin,
      canMakePublic: isOwner && isCalculated && !demo && !strictlyPublic,
      canMakePrivate: isOwner && (strictlyPublic || demo),
      canToggleDemoKit: strictlyPublic || demo || isOwner,
      canViewDemoKitUsage: demo,
      isDemoKitToggleDisabled: !demo && !isCalculated,
      isViewOnly: !isOwner
    };
  }

  return viewOnly();
};
